import {
  CONSUMER_WEBHOOK_DELIVERY_ERROR_LABELS,
  CONSUMER_WEBHOOK_URL_REJECTION_LABELS,
  CONSUMER_ERROR_CODES,
  type ConsumerWebhook,
  type ConsumerWebhookDelivery,
  type ConsumerWebhookUrlRejection,
} from '@easyhelp/shared';
import type { ApiError } from '../../../api/client';

/** Stato della notifica per la console: mai attivata, attiva o disattivata (la configurazione resta). */
export type WebhookStatus = 'non_attiva' | 'attiva' | 'disattivata';

export const WEBHOOK_STATUS_LABELS: Record<WebhookStatus, string> = {
  non_attiva: 'Non attiva',
  attiva: 'Attiva',
  disattivata: 'Disattivata',
};

export function webhookStatus(webhook: ConsumerWebhook | null): WebhookStatus {
  if (!webhook) return 'non_attiva';
  return webhook.enabled ? 'attiva' : 'disattivata';
}

/**
 * Il motivo, in italiano, per cui l'API ha rifiutato l'indirizzo (`consumers.webhook_url_not_allowed`,
 * `details.reason`), da mostrare sotto il campo; `null` per gli altri errori.
 */
export function urlRejectionOf(error: ApiError | null): string | null {
  if (error?.code !== CONSUMER_ERROR_CODES.webhookUrlNotAllowed) return null;
  const reason = error.details?.reason;
  return typeof reason === 'string' && reason in CONSUMER_WEBHOOK_URL_REJECTION_LABELS
    ? CONSUMER_WEBHOOK_URL_REJECTION_LABELS[reason as ConsumerWebhookUrlRejection]
    : error.message;
}

/** Ultima risposta di una consegna: codice HTTP e motivo del fallimento, se ci sono. */
export function deliveryResponseLabel(delivery: ConsumerWebhookDelivery): string {
  const parts = [
    delivery.lastResponseStatus === null ? null : `HTTP ${delivery.lastResponseStatus}`,
    delivery.lastError === null ? null : CONSUMER_WEBHOOK_DELIVERY_ERROR_LABELS[delivery.lastError],
  ].filter((part): part is string => part !== null);
  if (parts.length > 0) return parts.join(' · ');
  if (delivery.attempts > 0) return 'Nessuna risposta registrata';
  return delivery.status === 'in_coda' ? 'Nessun tentativo ancora' : 'Nessun invio';
}

/**
 * Riscontro dell'evento di prova, aggiornato man mano che la consegna procede; `stale` quando la
 * console ha smesso di seguirlo ed è ancora in coda.
 */
export function testFeedback(
  delivery: ConsumerWebhookDelivery,
  stale = false
): {
  severity: 'info' | 'success' | 'warning' | 'error';
  message: string;
} {
  switch (delivery.status) {
    case 'in_coda':
      return stale
        ? { severity: 'warning', message: 'Evento di prova ancora in coda: premi «Aggiorna» per rileggere l’esito.' }
        : { severity: 'info', message: 'Evento di prova accodato: l’esito compare qui entro pochi secondi.' };
    case 'consegnato':
      return {
        severity: 'success',
        message: `Evento di prova consegnato: il ricevitore ha risposto ${deliveryResponseLabel(delivery)}.`,
      };
    case 'fallito':
      return { severity: 'error', message: `Evento di prova non consegnato: ${deliveryResponseLabel(delivery)}.` };
    case 'scartato':
      return {
        severity: 'warning',
        message: 'Evento di prova scartato: la notifica è stata disattivata o il consumer revocato prima dell’invio.',
      };
  }
}
